import { Grid, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { FormikErrors, FormikTouched, setNestedObjectValues, useFormikContext } from 'formik';
import {
  ReprintCidFormVM,
  SearchPersonVM,
  IdentityCardVM,
} from 'front-office/view-models/reprint-cid/form';
import { Fragment, memo, useEffect } from 'react';
import AccordionPerson from './accordion-person';
import AccordionIdentityCard from './accordion-identity-card';
import { PersonResponse, VisibilityType } from 'front-office/pages/reprint-cid';

interface FormProps {
  initialValuesIdentityCard?: IdentityCardVM;
  initialValuesPerson?: SearchPersonVM;
  printingInterior: (values: ReprintCidFormVM) => void;
  printingExternal: (values: ReprintCidFormVM) => void;
  printingCardboard: (values: ReprintCidFormVM) => void;
  printingEnvelope: (values: ReprintCidFormVM) => void;
  castedAnprData: PersonResponse;
  visibility: VisibilityType;
  setVisibility: (value: VisibilityType) => void;
}

const FormComponent = ({
  initialValuesIdentityCard,
  initialValuesPerson,
  printingInterior,
  printingExternal,
  printingCardboard,
  printingEnvelope,
  castedAnprData,
  visibility,
  setVisibility,
}: FormProps): React.ReactElement => {
  const formContext = useFormikContext<ReprintCidFormVM>();

  useEffect(() => {
    if (initialValuesIdentityCard) {
      formContext.setFieldValue('identityCard', {
        ...(initialValuesIdentityCard || {}),
      });
    }
  }, [initialValuesIdentityCard]);

  useEffect(() => {
    if (initialValuesPerson) {
      formContext.setFieldValue('person', {
        ...(initialValuesPerson || {}),
      });

      return;
    }

    if (((initialValuesPerson as SearchPersonVM | undefined)?.list || []).length > 1) {
      formContext.setFieldValue('person', {
        ...formContext.values.person,
        list: (initialValuesPerson as SearchPersonVM | undefined)?.list,
      });
    }
  }, [initialValuesPerson]);

  const onChangePerson = useEventCallback(
    (key: keyof SearchPersonVM, value: SearchPersonVM[keyof SearchPersonVM]) => {
      formContext.setFieldValue('person', {
        ...(formContext.values.person || {}),
        [key]: value,
      });
    }
  );

  const onChangeIdentityCard = useEventCallback(
    (key: keyof IdentityCardVM, value: IdentityCardVM[keyof IdentityCardVM]) => {
      formContext.setFieldValue('identityCard', {
        ...(formContext.values.identityCard || {}),
        [key]: value,
      });
    }
  );

  return (
    <Fragment>
      <AccordionPerson
        onChange={onChangePerson}
        values={formContext.values.person || {}}
        castedAnprData={castedAnprData}
        visibility={visibility}
        setVisibility={setVisibility}
        disabled
      />
      <AccordionIdentityCard
        onChange={onChangeIdentityCard}
        values={formContext.values.identityCard || {}}
        errors={formContext.errors.identityCard as FormikErrors<IdentityCardVM>}
        touched={formContext.touched.identityCard as unknown as FormikTouched<IdentityCardVM>}
      />
      <Grid container spacing={2}>
        {formContext.values.identityCard?.isPaper ? (
          <Fragment>
            <Grid item xs={12} md={4}>
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                fullWidth
                onClick={() => printingInterior(formContext.values)}
              >
                Stampa interno
              </PmiButton>
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                fullWidth
                onClick={() => printingExternal(formContext.values)}
              >
                Stampa esterno
              </PmiButton>
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                fullWidth
                onClick={() => printingCardboard(formContext.values)}
              >
                Stampa cartoncino
              </PmiButton>
            </Grid>
          </Fragment>
        ) : undefined}
        <Grid item xs={12} md={4}>
          <PmiButton
            color="cdmBrandThird"
            variant="outlined"
            fullWidth
            onClick={() => {
              formContext.validateForm(formContext.values).then((validationErrors) => {
                formContext.setTouched(setNestedObjectValues(validationErrors, true));

                if (Object.values(validationErrors).length > 0) {
                  return;
                }

                printingEnvelope(formContext.values);
              });
            }}
          >
            Stampa busta
          </PmiButton>
        </Grid>
      </Grid>
    </Fragment>
  );
};

FormComponent.displayName = 'Reprint CID Page - Form';

const Form = memo(FormComponent);

export default Form;
